import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Activity, Clipboard, ExternalLink, Flame, Play, RefreshCw, Repeat2, RotateCcw,
  Save, ShieldCheck, Square, TestTube2, Wrench
} from "lucide-react";
import { toast } from "sonner";
import { retryProfileCommand, runProfileAction, setProfileMaintenance, updateProfileConfig } from "../../api/profiles";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import { StatusBadge } from "../../components/status-badge";
import type { ProfileDetailResponse } from "../../lib/types";
import { formatDate, shortText } from "../../lib/utils";

const textFields = [
  ["name", "名称"], ["fingerprint_seed", "指纹种子"], ["timezone", "时区"], ["locale", "语言"],
  ["user_agent", "User-Agent"], ["screen_width", "屏幕宽"], ["screen_height", "屏幕高"],
  ["gpu_vendor", "GPU Vendor"], ["gpu_renderer", "GPU Renderer"], ["hardware_concurrency", "CPU 线程"],
  ["color_scheme", "颜色模式"], ["notes", "备注"]
] as const;
const boolFields = [
  ["humanize", "拟人化"], ["headless", "无头模式"], ["geoip", "GeoIP"],
  ["clipboard_sync", "剪贴板同步"], ["auto_launch", "自动启动"]
] as const;

function formatBytes(value?: number | null) {
  if (!value && value !== 0) return "-";
  const units = ["B", "KB", "MB", "GB"];
  let size = value;
  let index = 0;
  while (size >= 1024 && index < units.length - 1) {
    size /= 1024;
    index += 1;
  }
  return `${size.toFixed(index ? 1 : 0)} ${units[index]}`;
}

function displayJson(value: unknown) {
  if (!value || (typeof value === "object" && !Object.keys(value as object).length)) return "尚无记录";
  return JSON.stringify(value, null, 2);
}

export function ProfileDetailPanel({ data }: { data: ProfileDetailResponse }) {
  const queryClient = useQueryClient();
  const { profile, manager, commands, events, active_task: activeTask } = data;
  const sourceConfig = useMemo(() => profile.profile_config_json || {}, [profile.profile_config_json]);
  const configuredProxy = String(sourceConfig.proxy || profile.manager_snapshot_json?.proxy || "");
  const [editing, setEditing] = useState(false);
  const [config, setConfig] = useState<Record<string, unknown>>(sourceConfig);
  const [proxyUrl, setProxyUrl] = useState("");
  const [warmupUrls, setWarmupUrls] = useState("https://discord.com/app");
  useEffect(() => setConfig(sourceConfig), [sourceConfig]);

  function refreshQueries() {
    void queryClient.invalidateQueries({ queryKey: ["profiles"] });
    void queryClient.invalidateQueries({ queryKey: ["profile", profile.id] });
  }

  const maintenance = useMutation({
    mutationFn: (enabled: boolean) => setProfileMaintenance(profile.id, enabled),
    onSuccess: ({ profile: updated }) => {
      toast.success(updated.maintenance_mode ? "已进入维护模式" : "已退出维护模式");
      refreshQueries();
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : "维护模式更新失败")
  });
  const action = useMutation({
    mutationFn: ({ name, params = {} }: { name: string; params?: Record<string, unknown> }) => runProfileAction(profile.id, name, params),
    onSuccess: () => {
      toast.success("Profile 操作已提交");
      refreshQueries();
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : "Profile 操作提交失败")
  });
  const saveConfig = useMutation({
    mutationFn: () => updateProfileConfig(profile.id, config),
    onSuccess: () => {
      toast.success("配置更新已提交");
      setEditing(false);
      refreshQueries();
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : "配置更新失败")
  });
  const retry = useMutation({
    mutationFn: retryProfileCommand,
    onSuccess: () => {
      toast.success("失败命令已重新排队");
      refreshQueries();
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : "命令重试失败")
  });

  async function copy(value: string, label: string) {
    await navigator.clipboard.writeText(value);
    toast.success(`${label}已复制`);
  }

  const disabled = Boolean(activeTask) || action.isPending;
  const maintenanceDisabled = disabled || !profile.maintenance_mode;
  const running = profile.status === "running";
  const diagnostic = JSON.stringify({ profile, manager, active_task: activeTask, commands: commands.slice(0, 20), events: events.slice(0, 50) }, null, 2);

  return (
    <div className="space-y-5 text-xs">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold">{profile.name || profile.external_profile_id || profile.id}</h3>
            <StatusBadge status={profile.status} />
            {profile.maintenance_mode ? <span className="rounded bg-violet-100 px-2 py-0.5 text-violet-800">维护中</span> : null}
          </div>
          <div className="mt-1 font-mono text-[11px] text-slate-500">{profile.external_profile_id || profile.id}</div>
        </div>
        <div className="flex flex-wrap justify-end gap-1.5">
          <Button onClick={() => action.mutate({ name: "refresh" })}><RefreshCw />刷新</Button>
          <Button disabled={Boolean(activeTask)} onClick={() => maintenance.mutate(!profile.maintenance_mode)}><Wrench />{profile.maintenance_mode ? "退出维护" : "进入维护"}</Button>
          <Button disabled={maintenanceDisabled || running} onClick={() => action.mutate({ name: "start" })}><Play />启动</Button>
          <Button disabled={maintenanceDisabled || !running} onClick={() => action.mutate({ name: "stop" })}><Square />停止</Button>
          <Button disabled={maintenanceDisabled || !running} onClick={() => action.mutate({ name: "restart" })}><RotateCcw />重启</Button>
        </div>
      </div>

      {activeTask ? <div className="border-l-2 border-blue-500 bg-blue-50 px-3 py-2 text-blue-900">当前任务：{activeTask.task_type} / {activeTask.status} / {activeTask.id}</div> : null}
      {profile.last_error ? <div className="border-l-2 border-rose-500 bg-rose-50 px-3 py-2 text-rose-900">{profile.last_error}</div> : null}

      <section className="border-t border-slate-200 pt-4">
        <h4 className="mb-3 font-semibold text-slate-900">概览</h4>
        <dl className="grid grid-cols-2 gap-x-5 gap-y-3 lg:grid-cols-4">
          <div><dt className="text-slate-500">账号绑定</dt><dd className="mt-1 font-medium">{profile.discord_account_id ? `#${profile.discord_account_id} ${profile.account_email || ""}` : "未绑定"}</dd></div>
          <div><dt className="text-slate-500">Manager 连接</dt><dd className="mt-1 font-medium">{profile.browser_manager_id} / {profile.manager_effective_status || manager?.effective_status || manager?.status || "未知"}</dd></div>
          <div><dt className="text-slate-500">目录占用</dt><dd className="mt-1 font-medium">{formatBytes(profile.disk_usage_bytes)} / {formatDate(profile.disk_usage_observed_at)}</dd></div>
          <div><dt className="text-slate-500">清单观测</dt><dd className="mt-1 font-medium">{profile.inventory_freshness} / {formatDate(profile.observed_at)}</dd></div>
          <div><dt className="text-slate-500">配置模板</dt><dd className="mt-1 font-medium">{profile.applied_template_id ? `${profile.applied_template_id} / v${profile.applied_template_version}` : "未记录"}</dd></div>
          <div><dt className="text-slate-500">创建任务</dt><dd className="mt-1 font-mono">{profile.created_by_task_id ? shortText(profile.created_by_task_id, 12) : "-"}</dd></div>
          <div><dt className="text-slate-500">最后任务</dt><dd className="mt-1 font-mono">{profile.last_task_id ? shortText(profile.last_task_id, 12) : "-"}</dd></div>
          <div><dt className="text-slate-500">最近启动</dt><dd className="mt-1">{formatDate(profile.last_started_at)}</dd></div>
          <div><dt className="text-slate-500">最近停止</dt><dd className="mt-1">{formatDate(profile.last_stopped_at)}</dd></div>
        </dl>
        <div className="mt-3 flex flex-wrap gap-1.5">
          <Button disabled={!running || !manager?.base_url} onClick={() => window.open(`${manager?.base_url || profile.novnc_url || ""}?profile=${profile.external_profile_id || ""}`, "_blank", "noopener,noreferrer")}><ExternalLink />打开 noVNC</Button>
          <Button disabled={!profile.cdp_ws_url} onClick={() => void copy(profile.cdp_ws_url || "", "CDP 地址")}><Clipboard />复制 CDP</Button>
          <Button onClick={() => void copy(diagnostic, "脱敏诊断包")}><ShieldCheck />复制诊断包</Button>
        </div>
      </section>

      <section className="border-t border-slate-200 pt-4">
        <div className="mb-3 flex items-center justify-between">
          <h4 className="font-semibold text-slate-900">Manager 有效配置</h4>
          <div className="flex gap-1.5">
            <Button disabled={!profile.maintenance_mode || Boolean(activeTask)} onClick={() => setEditing(!editing)}>{editing ? "取消" : "编辑"}</Button>
            {editing ? <Button className="bg-slate-900 text-white hover:bg-slate-800" onClick={() => saveConfig.mutate()} disabled={saveConfig.isPending}><Save />保存</Button> : null}
          </div>
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {textFields.map(([key, label]) => (
            <label key={key} className={key === "user_agent" || key === "gpu_renderer" || key === "notes" ? "md:col-span-2" : ""}>
              <span className="mb-1 block text-slate-500">{label}</span>
              <input className="h-8 w-full rounded-md border border-slate-300 px-2 font-mono text-xs disabled:bg-slate-50" disabled={!editing} value={String(config[key] ?? "")} onChange={(event) => setConfig((old) => ({ ...old, [key]: ["fingerprint_seed", "screen_width", "screen_height", "hardware_concurrency"].includes(key) ? Number(event.target.value) : event.target.value }))} />
            </label>
          ))}
          <label><span className="mb-1 block text-slate-500">平台</span><select className="h-8 w-full rounded-md border border-slate-300 bg-white px-2" disabled={!editing} value={String(config.platform || "windows")} onChange={(event) => setConfig((old) => ({ ...old, platform: event.target.value }))}><option value="windows">Windows</option><option value="macos">macOS</option><option value="linux">Linux</option></select></label>
          <label><span className="mb-1 block text-slate-500">拟人预设</span><select className="h-8 w-full rounded-md border border-slate-300 bg-white px-2" disabled={!editing} value={String(config.human_preset || "careful")} onChange={(event) => setConfig((old) => ({ ...old, human_preset: event.target.value }))}><option value="default">default</option><option value="careful">careful</option></select></label>
          <label className="md:col-span-2"><span className="mb-1 block text-slate-500">启动参数（每行一项）</span><textarea className="min-h-20 w-full rounded-md border border-slate-300 p-2 font-mono text-xs disabled:bg-slate-50" disabled={!editing} value={Array.isArray(config.launch_args) ? config.launch_args.join("\n") : ""} onChange={(event) => setConfig((old) => ({ ...old, launch_args: event.target.value.split("\n").map((item) => item.trim()).filter(Boolean) }))} /></label>
        </div>
        <div className="mt-3 flex flex-wrap gap-4">
          {boolFields.map(([key, label]) => <label key={key} className="flex items-center gap-2"><Checkbox disabled={!editing} checked={Boolean(config[key])} onCheckedChange={(checked) => setConfig((old) => ({ ...old, [key]: checked === true }))} />{label}</label>)}
        </div>
      </section>

      <section className="border-t border-slate-200 pt-4">
        <h4 className="mb-3 font-semibold text-slate-900">通用能力</h4>
        <div className="grid gap-4 lg:grid-cols-2">
          <div>
            <div className="mb-2 flex items-center gap-2 font-medium"><Activity />代理检测</div>
            <div className="mb-2 rounded-md border border-slate-200 bg-white p-2">
              <div className="text-slate-500">当前配置代理</div>
              <div className="mt-1 break-all font-mono text-[11px]">{configuredProxy || "未配置"}</div>
            </div>
            <pre className="max-h-40 overflow-auto whitespace-pre-wrap rounded-md bg-slate-50 p-2 text-[11px] leading-5">{displayJson(profile.last_proxy_check_json)}</pre>
            <div className="mt-2 flex gap-1.5"><Button disabled={disabled} onClick={() => action.mutate({ name: "proxy_test" })}><TestTube2 />重新测速</Button></div>
            <div className="mt-2 flex gap-1.5"><input className="h-8 min-w-0 flex-1 rounded-md border border-slate-300 px-2 font-mono" type="password" placeholder="socks5://user:pass@host:port" value={proxyUrl} onChange={(event) => setProxyUrl(event.target.value)} /><Button disabled={maintenanceDisabled || !proxyUrl} onClick={() => action.mutate({ name: "change_proxy", params: { proxy_url: proxyUrl } })}><Repeat2 />测速并更换</Button></div>
          </div>
          <div>
            <div className="mb-2 flex items-center gap-2 font-medium"><Flame />Assets 预热</div>
            <pre className="max-h-40 overflow-auto whitespace-pre-wrap rounded-md bg-slate-50 p-2 text-[11px] leading-5">{displayJson(profile.last_warmup_json)}</pre>
            <div className="mt-2 flex gap-1.5"><input className="h-8 min-w-0 flex-1 rounded-md border border-slate-300 px-2" value={warmupUrls} onChange={(event) => setWarmupUrls(event.target.value)} /><Button disabled={maintenanceDisabled} onClick={() => action.mutate({ name: "warmup", params: { assets_warmup_urls: warmupUrls } })}><Flame />执行预热</Button></div>
          </div>
        </div>
        <div className="mt-4 border-t border-slate-100 pt-3"><Button disabled={maintenanceDisabled} onClick={() => action.mutate({ name: "replace" })}><Repeat2 />创建替代 Profile</Button></div>
      </section>

      <section className="border-t border-slate-200 pt-4">
        <h4 className="mb-3 font-semibold text-slate-900">操作命令</h4>
        <div className="overflow-auto border border-slate-200">
          <table className="w-full text-left"><thead className="bg-slate-50 text-slate-500"><tr><th className="p-2">操作</th><th className="p-2">状态</th><th className="p-2">次数</th><th className="p-2">结果</th><th className="p-2">时间</th><th className="p-2"></th></tr></thead><tbody>{commands.map((command) => <tr key={command.id} className="border-t"><td className="p-2">{command.action}</td><td className="p-2"><StatusBadge status={command.status} /></td><td className="p-2">{command.attempts}</td><td className="max-w-sm break-words p-2">{command.error_message || String(command.result_json?.message || "-")}</td><td className="p-2">{formatDate(command.updated_at)}</td><td className="p-2">{command.status === "failed" ? <Button onClick={() => retry.mutate(command.id)}>重试</Button> : null}</td></tr>)}</tbody></table>
          {!commands.length ? <div className="p-4 text-center text-slate-500">暂无操作命令</div> : null}
        </div>
      </section>

      <section className="border-t border-slate-200 pt-4">
        <h4 className="mb-3 font-semibold text-slate-900">历史事件</h4>
        <div className="max-h-80 overflow-auto border border-slate-200">{events.map((event) => <div key={event.id} className="border-b p-2 last:border-b-0"><div className="flex justify-between gap-3 text-slate-500"><span>{event.source} / {event.event_type}</span><span>{formatDate(event.created_at)}</span></div><div className="mt-1 whitespace-pre-wrap break-words">{event.message}</div></div>)}{!events.length ? <div className="p-4 text-center text-slate-500">暂无历史事件</div> : null}</div>
      </section>
    </div>
  );
}
